import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  OnInit,
  Output,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { countries } from '@core/constants/countries';
import intlTelInput, { Iti, SomeOptions } from 'intl-tel-input';
import { OrderInterface } from '@interfaces/orders/order-interface';
import { OrdersService } from '@core/services/orders/orders.service';
import { LnPrimaryButtonComponent } from '@components/butons/ln-primary-button/ln-primary-button.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiResponse } from '@interfaces/api/api.interface';
import { FullScreenModalComponent } from '@components/modals/full-screen-modal/full-screen-modal.component';
import { SuccessfulOrderCardComponent } from '@components/cards/successful-order-card/successful-order-card.component';
import { MenuActions } from '../../states/menu_actions';
import { ShoppingCartState } from '../../states/shopping-cart-state';
import { FormContactState } from '../../states/form-contact-state';
import { TranslocoPipe } from '@jsverse/transloco';
import { CaptchaComponent } from '@components/captcha/captcha.component';

@Component({
  selector: 'ln-ln-form-contact',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    ReactiveFormsModule,
    LnPrimaryButtonComponent,
    FullScreenModalComponent,
    SuccessfulOrderCardComponent,
    TranslocoPipe,
    CaptchaComponent,
  ],
  templateUrl: './ln-form-contact.component.html',
  styleUrls: ['./ln-form-contact.component.css'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class LnFormContactComponent implements OnInit, AfterViewInit {
  private readonly TAG = 'LnFormContactComponent';
  @ViewChild('phoneInput', { static: false }) phoneInput!: ElementRef;
  contadorCaracteresMessage = 0;
  form: FormGroup;
  countries = countries;
  iti?: Iti;
  private orderService = inject(OrdersService);
  protected enviarButtonShowLoading: WritableSignal<boolean> = signal(false);
  showSuccessfulModal: WritableSignal<boolean> = signal(false);
  @Output() showUnSuccessfulModal = new EventEmitter<void>();
  private shoppingCartState = inject(ShoppingCartState);
  private destroyRef = inject(DestroyRef);
  private menuActions = inject(MenuActions);
  private formPreviousData = inject(FormContactState);
  protected captchaToken: WritableSignal<string | undefined> = signal(undefined);
  constructor() {
    // '^[A-Za-zÀ-ÿ0-9-\\s]+$'
    this.form = new FormGroup({
      name: new FormControl('', [Validators.required, Validators.pattern(/^[\p{L}\p{N}\s'’-]+$/u)]),
      phonePrefix: new FormControl('', [Validators.required]),
      phoneNumber: new FormControl('', [Validators.required, Validators.pattern('^[0-9]+$')]),
      email: new FormControl('', [Validators.required, Validators.email]),
      message: new FormControl('', [Validators.maxLength(250)]),
      sendCopy: new FormControl(true),
    });
  }
  ngOnInit(): void {
    this.addPMessageInputListener();
    this.restorePreviousData();
    this.updateFormDataState();
  }

  restorePreviousData(): void {
    const savedData = this.formPreviousData.getFormData();
    if (savedData) {
      this.form.patchValue(savedData);
    }
  }

  updateFormDataState() {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      this.formPreviousData.setFormData(value);
    });
  }

  ngAfterViewInit() {
    if (this.phoneInput) {
      // , utilsScript: 'assets/utils.js'
      const options: SomeOptions = { initialCountry: 'gt' };
      this.iti = intlTelInput(this.phoneInput.nativeElement, options);
      //set initial Guatemala dial code
      this.setDialCode('502');
      this.addPhoneInputListener();
    } else {
      console.warn('phoneInput no encontrado en ngAfterViewInit');
    }
  }
  addPMessageInputListener() {
    this.form.controls['message'].valueChanges.subscribe(value => {
      this.contadorCaracteresMessage = value.length;
    });
  }
  addPhoneInputListener() {
    this.phoneInput.nativeElement.addEventListener('countrychange', () => {
      const dialCode: string | undefined = this.iti?.getSelectedCountryData().dialCode;
      console.log('Código de país:', dialCode);
      if (dialCode) {
        this.setDialCode(dialCode);
      }
    });
  }
  setDialCode(dialCode: string) {
    this.form.get('phonePrefix')?.setValue(dialCode, { emitEvent: false });
  }

  preventTyping(event: KeyboardEvent) {
    event.preventDefault(); // Stops any character input
  }

  onSubmit(): void {
    if (this.form.valid && this.captchaToken()?.length) {
      const dialCode = this.iti?.getSelectedCountryData().dialCode;
      const phoneNumber = this.form.value.phoneNumber;
      const fullPhone = `+${dialCode}${phoneNumber}`;
      const mesagge: string = this.form.value.message;
      const order: OrderInterface = {
        nombreCompleto: this.form.value.name,
        telefono: fullPhone,
        email: this.form.value.email,
        mensaje: mesagge.length ? mesagge : undefined,
        sendMeACopy: this.form.value.sendCopy,
      };
      this.enviarButtonShowLoading.set(true);
      this.orderService
        .createOrder(order, this.captchaToken())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res: ApiResponse<void>) => {
            if (res.success) {
              this.showSuccessfulModal.set(true);
              this.enviarButtonShowLoading.set(false);
              this.formPreviousData.clear();
            } else {
              console.log(`${this.TAG} orderService error`);
              this.showUnSuccessfulModal.emit();
            }
          },
          error: err => {
            this.enviarButtonShowLoading.set(false);
            this.showUnSuccessfulModal.emit();
            console.log(`${this.TAG} orderService error: ${err}`);
          },
        });
    } else {
      this.form.markAllAsTouched();
    }
  }

  get selectedDialCode(): string {
    const selected = this.countries.find(c => c.code === this.form.get('country')?.value);
    return selected ? selected.code : '';
  }

  get fullPhone(): string {
    return `${this.selectedDialCode}${this.form.get('phoneNumber')?.value || ''}`;
  }
  closeSuccessfulModal(): void {
    this.showSuccessfulModal.set(false);
    this.shoppingCartState.getShoppingCartItems();
    this.menuActions.goHome();
  }
}
